import * as React from 'react';
import { FaRegCommentDots, FaPlus, FaTrash, FaTimes } from 'react-icons/fa';
import type { ChatSession } from '../../types';
import './SessionSidebar.scss';

interface SessionSidebarProps {
  sessions: ChatSession[];
  currentSessionId: string | null;
  onSessionSelect: (sessionId: string) => void;
  onNewSession: () => void;
  onDeleteSession: (sessionId: string) => void;
  onClose: () => void;
  isOpen: boolean;
  theme: 'dark' | 'light';
  mode: 'widget' | 'standalone';
}

const SessionSidebar: React.FC<SessionSidebarProps> = ({
  sessions,
  currentSessionId,
  onSessionSelect,
  onNewSession,
  onDeleteSession,
  onClose,
  isOpen,
  theme,
  mode,
}) => {
  // Calculate sidebar width based on mode
  const sidebarWidth = mode === 'widget' ? '12rem' : '20rem';
  
  // Enhanced close handlers for UMD compatibility
  const handleOverlayClick = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  }, [onClose]);

  const handleCloseClick = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onClose();
  }, [onClose]);

  // Prevent sidebar clicks from bubbling to overlay
  const handleSidebarClick = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  // Escape key handler
  React.useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      return () => document.removeEventListener('keydown', handleEscapeKey);
    }
  }, [isOpen, onClose]);
  
  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div 
          className="session-sidebar__overlay"
          onClick={handleOverlayClick}
          onMouseDown={handleOverlayClick}
          role="button"
          tabIndex={-1}
          aria-label="Close sidebar"
        />
      )}
      {/* Sidebar */}
      <div
        className={`session-sidebar ${isOpen ? 'session-sidebar--open' : ''}`}
        style={{
          background: theme === 'dark' ? '#232a36' : '#fff',
          width: sidebarWidth,
          height: mode === 'widget' ? 'inherit' : '100%',
        }}
        onClick={handleSidebarClick}
      >
        {/* Header */}
        <div className="session-sidebar__header">
          <h2>Chat History</h2>
          <div className="session-sidebar__header-actions">
            <button
              onClick={onNewSession}
              className="session-sidebar__header-button"
              title="New Chat"
            >
              <FaPlus size={18} />
            </button>
            <button
              onClick={handleCloseClick}
              className="session-sidebar__header-button session-sidebar__header-button--close"
              title="Close"
              type="button"
            >
              <FaTimes size={18} />
            </button>
          </div>
        </div>

        {/* Sessions List */}
        <div className="session-sidebar__list">
          {sessions.length === 0 ? (
            <div className="session-sidebar__list--empty">
              <FaRegCommentDots />
              <p>No chat history yet</p>
            </div>
          ) : (
            sessions.map((session) => (
              <div
                key={session.id}
                className={`session-sidebar__item ${
                  currentSessionId === session.id 
                    ? 'session-sidebar__item--active' 
                    : ''
                }`}
                onClick={() => onSessionSelect(session.id)}
              >
                <div className="session-sidebar__item-icon">
                  <FaRegCommentDots size={16} />
                </div>
                
                <div className="session-sidebar__item-content">
                  <p className="session-sidebar__item-title">
                    {session.title}
                  </p>
                  <p className="session-sidebar__item-meta">
                    <span>{session.messages.length} message{session.messages.length !== 1 ? 's' : ''}</span>
                    <span>{session.updatedAt.toLocaleDateString()}</span>
                  </p>
                </div>
                
                <div className="session-sidebar__item-actions">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteSession(session.id);
                    }}
                    className="session-sidebar__item-delete"
                    title="Delete Session"
                  >
                    <FaTrash size={14} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="session-sidebar__footer">
          <p>OneAI Chatbot</p>
          <p>{sessions.length} conversation{sessions.length !== 1 ? 's' : ''}</p>
        </div>
      </div>
    </>
  );
};

export { SessionSidebar };
